import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, HostListener, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ClientInfoComponent } from '../client-info/client-info.component';
import { ClientSessionService, UiSession } from '@brighthub/uisession';
import { ClientInfoSkeletonComponent } from '../client-info-skeleton/client-info-skeleton.component';
import { ClientSearchComponent } from '../client-search/client-search.component';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { CopilotFacadeService } from '@brighthub/copilot';
import { filter, map, take } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { ClientSearchService } from '../client-search.service';
import { GenericResponseCode } from '@brighthub/api';
import { MessageService } from 'primeng/api';
import { Tooltip } from 'primeng/tooltip';
import { ClientQueueStateService } from '../client-queue/client-queue-state.service';
import { ClientQueueEnrichmentService } from '../client-queue/client-queue-enrichment.service';
import { ClientQueueRowComponent } from '../client-queue/client-queue-row.component';
import {
  EnrichedQueueRow,
  QUEUE_FILTER_CHIPS,
  QueueFilter,
  QueueRowActionEvent,
} from '../client-queue/client-queue.model';
import { ClientContactService } from '../client-contact/client-contact.service';
import { ClientQualityStateService } from '../client-quality/client-quality-state.service';
import { QualityRisk } from '../client-quality/client-quality.model';

@Component({
  selector: 'bai-client-list',
  standalone: true,
  imports: [
    CommonModule,
    ClientInfoComponent,
    ClientInfoSkeletonComponent,
    ScrollPanelModule,
    Tooltip,
    ClientQueueRowComponent,
  ],
  providers: [ClientQueueStateService, ClientQueueEnrichmentService, ClientQualityStateService],
  styleUrls: ['./client-list.component.scss'],
  templateUrl: './client-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientListComponent implements OnInit {
  @ViewChild('clientListScrollPanel') clientListScrollPanelRef?: ElementRef;

  readonly clientService = inject(ClientSessionService);
  readonly facade = inject(CopilotFacadeService);
  private readonly route = inject(ActivatedRoute);
  private readonly clientSearch = inject(ClientSearchService);
  private readonly _messageService = inject(MessageService);
  private readonly _contactService = inject(ClientContactService);
  readonly queueState = inject(ClientQueueStateService);
  private readonly _qualityState = inject(ClientQualityStateService);
  readonly showArchived = this.clientService.showArchived;

  highlightedClientIds$ = this.facade.highlightedClientIds$;
  highlightedConversations$ = this.facade.highlightedConversations$;
  readonly selectedConversation = this.clientService.selectedConversationId;
  private readonly _qualityRiskBySession = signal<Record<string, QualityRisk>>({});

  selectedConversation$ = this.facade.selectedConversation$;
  @Input() collapsed = false;

  /** All filter options. */
  readonly filterChips = QUEUE_FILTER_CHIPS;

  /** Whether the filter dropdown is open. */
  readonly showFilterDropdown = signal(false);

  /** Label of the currently active filter, prefixed for display. */
  readonly activeFilterLabel = computed(() =>
    this.filterChips.find((c) => c.id === this.queueState.activeFilter())?.label ?? 'All'
  );

  // Signal to store the `guid` from the query param
  private readonly _guidParam = signal<string | null>(null);
  private _handledGuid = false; // Used to run the effect only once


  constructor() {
    this.route.queryParamMap
      .pipe(
        map((params) => params.get('guid')),
        filter((guid): guid is string => !!guid),
        take(1)
      )
      .subscribe((guid) => {
        this._guidParam.set(guid);
      });

    effect(() => {

      const guid = this._guidParam();
      const ready = this.clientService.initialized(); // <-- wait for first load done
      if (this._handledGuid || !guid || !ready) return;
      this._handledGuid = true;

      const sessions = this.clientService.sessions();
      const existing = sessions.find((s) => s.subjectData.guid === guid);

      if (existing) {
        this.onClientSelected(existing);
        return;
      }

      this.clientSearch.searchByGuid(guid).subscribe({
        next: (res) => {
          if (
            res.result.code === GenericResponseCode.SERVICE_OK &&
            res.payload.guid
          ) {
            this.clientService.createOrSelectClientConversation(res.payload.guid);
          } else {
            this._showSearchError(`No client found for ID ${guid}`);
          }
        },
        error: (err) => {
          const msg =
            err.status === 404
              ? `No client found for ID ${guid}`
              : `Error searching for client: ${err.message || 'Unknown error'}`;
          this._showSearchError(msg);
        },
      });
    });

    effect(() => {
      const selected = this.clientService.selectedSession();
      if (selected) {
        this.onClientSelected(selected);
      }
    });

    effect(() => {
      this.facade.disableInput.set(this.clientService.showArchived());
    });

    // Keep quality badge lookup precomputed so template reads are pure.
    effect(() => {
      const sections = this.queueState.sections();
      const next: Record<string, QualityRisk> = {};
      for (const section of sections) {
        for (const row of section.rows) {
          const sessionId = row.session.uiSessionId;
          if (!sessionId) continue;
          this._qualityState.ensureState(sessionId);
          next[sessionId] = this._qualityState.getState(sessionId).qualityRisk;
        }
      }
      this._qualityRiskBySession.set(next);
    });
  }

  ngOnInit() {
    this.clientService.loadClientSessions();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.showFilterDropdown.set(false);
  }

  toggleFilterDropdown(event: Event): void {
    event.stopPropagation();
    this.showFilterDropdown.update((v) => !v);
  }

  private _showSearchError(message: string) {
    this._messageService.add({
      severity: 'error',
      detail: message,
      closable: true,
      life: 60000,
    });
  }

  onSelect(uiSession: UiSession) {
    const subjectId = uiSession.subjectId;
    const conversationId = uiSession.conversationId || '';

    this.clientService.setSelectedSession(uiSession);
    this.clientService.setSelectedConversation(conversationId);

    this.facade.selectClient(subjectId);
    this.facade.loadAndSelectConversation(
      subjectId,
      conversationId,
      uiSession.state,
      true
    );
    this.scrollToSelected(uiSession.conversationId ?? '');
    this.clientService.setSelectedSubject(uiSession.subjectData);
    this.clientService.rememberLastSession(uiSession);
  }

  onClientSelected(uiSession: UiSession) {
    this.onSelect(uiSession);
    this.scrollToSelected(uiSession.conversationId ?? '');
  }

  scrollToSelected(conversationId: string) {
    queueMicrotask(() => {
      const host = this.clientListScrollPanelRef?.nativeElement as HTMLElement | undefined;
      const container = host?.querySelector('.p-scrollpanel-content');
      const target = container?.querySelector(
        `[data-conversation="${conversationId}"]`
      );

      if (container instanceof HTMLElement && target instanceof HTMLElement) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();

        const offsetTop =
          targetRect.top - containerRect.top + container.scrollTop;

        container.scrollTo({
          top: offsetTop - 12,
          behavior: 'smooth',
        });

        target.focus?.();
      }
    });
  }

  viewArchived() {
    this.clientService.toggleArchivedView();
    const view = this.showArchived() ? 'archived' : 'active';
    this.clientService.restorePreviousSessionOrClear(view);
  }

  setFilter(filter: QueueFilter): void {
    this.queueState.setFilter(filter);
  }

  onRowAction(event: QueueRowActionEvent): void {
    const subjectGuid = event.row.session.subjectData.guid;
    switch (event.action) {
      case 'open_chat':
        this.onSelect(event.row.session);
        break;
      case 'assign_to_me':
        this.queueState.assignToMe(event.row);
        break;
      case 'snooze':
        this.queueState.snooze(event.row);
        break;
      case 'archive':
        this.queueState.archive(event.row);
        break;
      case 'call_client': {
        const contact = this._contactService.resolveContact(event.row.session.subjectData);
        this._contactService.openPhone(contact.primaryPhone, 'queue_menu', subjectGuid);
        break;
      }
      case 'email_client': {
        const contact = this._contactService.resolveContact(event.row.session.subjectData);
        this._contactService.openEmail(contact.primaryEmail, 'queue_menu', subjectGuid);
        break;
      }
    }
  }

  onQueueRowSelect(row: EnrichedQueueRow): void {
    this.onSelect(row.session);
  }

  qualityRiskFor(row: EnrichedQueueRow): QualityRisk {
    const sessionId = row.session.uiSessionId;
    if (!sessionId) return 'none';
    return this._qualityRiskBySession()[sessionId] ?? 'none';
  }

}
